// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import { Textarea } from '@/components/ui/Textarea'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import {
  useDebtCase,
  useEscalateDebtCase,
  useResolveDebtCase,
  useAddDebtCaseNote,
  useMarkNoticeSent,
} from '@/hooks/useDebtCases'
import { useNotificationStore } from '@/stores'
import type { DebtCase, DebtCaseStage } from '@/types'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STAGE_ORDER: DebtCaseStage[] = ['notice_1', 'notice_2', 'final_notice', 'legal', 'resolved']

function getNextStage(current: DebtCaseStage): DebtCaseStage | null {
  const idx = STAGE_ORDER.indexOf(current)
  if (idx === -1 || idx >= STAGE_ORDER.length - 2) return null
  return STAGE_ORDER[idx + 1]
}

function getStageBadgeVariant(stage: DebtCaseStage): 'warning' | 'error' | 'success' | 'ghost' {
  switch (stage) {
    case 'notice_1':
    case 'notice_2':
      return 'warning'
    case 'final_notice':
    case 'legal':
      return 'error'
    case 'resolved':
      return 'success'
    default:
      return 'ghost'
  }
}

// DAT-1: `due_at` is one of the date-only fields CLAUDE.md documents as
// stored midnight-UTC of the intended day: so the intended day is read via
// UTC getters (not local ones), and the offset is added and read back
// entirely in the UTC frame (Date.UTC + toISOString), never mixing in the
// runtime's own local timezone. That avoids the DST-crossing drift a
// local-getDate/setDate round trip would be exposed to over a 60/90-day
// window.
function addDaysToUtcDateOnly(isoDateTime: string, days: number): string {
  const d = new Date(isoDateTime)
  const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days))
  return next.toISOString()
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface TimelineEvent {
  date: string
  label: string
  isPast: boolean
}

function EscalationTimeline({
  debtCase,
  t,
  formatDate,
}: {
  debtCase: DebtCase
  t: (key: string, opts?: Record<string, string>) => string
  formatDate: (dateStr: string | null | undefined) => string
}) {
  const events: TimelineEvent[] = []

  events.push({
    date: formatDate(debtCase.created_at),
    label: t('timeline.case_created'),
    isPast: true,
  })

  if (debtCase.escalated_at && debtCase.stage !== 'notice_1') {
    events.push({
      date: formatDate(debtCase.escalated_at),
      label: t('timeline.auto_escalated', { stage: t(`stages.${debtCase.stage}`) }),
      isPast: true,
    })
  }

  // Project next auto-escalation if applicable
  if (debtCase.stage === 'notice_1' && debtCase.invoice?.due_at) {
    const nextDateStr = addDaysToUtcDateOnly(debtCase.invoice.due_at, 60)
    events.push({
      date: formatDate(nextDateStr),
      label: t('timeline.upcoming', {
        stage: t('stages.notice_2'),
        date: formatDate(nextDateStr),
      }),
      isPast: false,
    })
  } else if (debtCase.stage === 'notice_2' && debtCase.invoice?.due_at) {
    const nextDateStr = addDaysToUtcDateOnly(debtCase.invoice.due_at, 90)
    events.push({
      date: formatDate(nextDateStr),
      label: t('timeline.upcoming', {
        stage: t('stages.final_notice'),
        date: formatDate(nextDateStr),
      }),
      isPast: false,
    })
  }

  if (debtCase.resolved_at) {
    events.push({
      date: formatDate(debtCase.resolved_at),
      label: t('timeline.resolved'),
      isPast: true,
    })
  }

  return (
    <ul className="space-y-3">
      {events.map((ev, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <div className="flex flex-col items-center mt-0.5">
            <div
              className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                ev.isPast
                  ? 'bg-primary border-primary'
                  : 'bg-base-100 border-base-300'
              }`}
            />
            {idx < events.length - 1 && (
              <div className="w-0.5 h-8 bg-base-300 mt-1" />
            )}
          </div>
          <div>
            <p className={`text-sm ${ev.isPast ? 'text-base-content' : 'text-base-content/50'}`}>
              {ev.label}
            </p>
            <p className="text-xs text-base-content/50">{ev.date}</p>
          </div>
        </li>
      ))}
    </ul>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function DebtCaseDetailPage() {
  const { t } = useTranslation('debt_collection')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const debtCaseId = Number(id)
  const navigate = useNavigate()

  const { data: debtCase, isLoading, isFetching } = useDebtCase(debtCaseId)
  // Account-aware formatter (calendar/timezone/date_format) — a raw ISO date
  // string in; '—' preserved for null/undefined to match the prior date-fns
  // helper's fallback.
  const rawFormatDate = useDateFormatter()
  const formatDate = (dateStr: string | null | undefined) => (dateStr ? rawFormatDate(dateStr) : '—')
  const escalate = useEscalateDebtCase()
  const resolve = useResolveDebtCase()
  const addNote = useAddDebtCaseNote()
  const markNoticeSent = useMarkNoticeSent()
  const addToast = useNotificationStore((s) => s.addToast)

  const [showEscalateModal, setShowEscalateModal] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [escalateToLegal, setEscalateToLegal] = useState(false)
  const [noteText, setNoteText] = useState('')

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card>
          <Skeleton className="h-48" />
        </Card>
      </div>
    )
  }

  if (!debtCase) {
    return (
      <Card>
        <EmptyState
          title={tc('errors.something_went_wrong')}
          action={
            <Button onClick={() => navigate('/debt-collection')}>{t('title')}</Button>
          }
        />
      </Card>
    )
  }

  const nextStage = getNextStage(debtCase.stage)
  const canEscalate = debtCase.stage !== 'resolved' && debtCase.stage !== 'legal'
  const canResolve = debtCase.stage !== 'resolved'

  const handleEscalate = async () => {
    await escalate.mutateAsync({
      id: debtCaseId,
      target_stage: escalateToLegal ? 'legal' : undefined,
    })
    setShowEscalateModal(false)
  }

  const handleResolve = async () => {
    await resolve.mutateAsync(debtCaseId)
    setShowResolveModal(false)
  }

  const handleAddNote = async () => {
    if (!noteText.trim()) return
    await addNote.mutateAsync({ id: debtCaseId, note: noteText.trim() })
    setNoteText('')
  }

  const handleMarkNoticeSent = async () => {
    await markNoticeSent.mutateAsync(debtCaseId)
  }

  const handleGenerateLegalNotice = () => {
    // TODO (T805): Wire up pdfme legal notice PDF generation after template is seeded
    // For now show a placeholder notification via toast
    addToast({ type: 'success', message: t('legal_notice_toast') })
  }

  const amountPaid = debtCase.invoice
    ? debtCase.invoice.total - debtCase.amount_due
    : 0

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/debt-collection' },
            { label: `${debtCase.invoice_number} ${debtCase.customer_name}` },
          ]}
        />

        <PageHeader
          title={t('detail.page_title', { invoice_number: debtCase.invoice_number })}
          // EP-106 Item 1 — the stage Badge isn't an action, so it moves out of
          // the actions row into the description slot (stays visible on every
          // breakpoint, unlike titleAction which is mobile-only).
          description={
            <span className="inline-flex flex-wrap items-center gap-2">
              <Badge variant={getStageBadgeVariant(debtCase.stage)}>
                {t(`stages.${debtCase.stage}`)}
              </Badge>
              <span>
                {debtCase.customer_name}
                {debtCase.unit_name ? ` · ${t('detail.unit_label', { unit: debtCase.unit_name })}` : ''}
                {debtCase.property_name ? ` · ${debtCase.property_name}` : ''}
              </span>
            </span>
          }
          isRefreshing={isFetching && !isLoading}
          overflowActions={[
            {
              key: 'escalate',
              label: t('actions.escalate'),
              onClick: () => {
                setEscalateToLegal(false)
                setShowEscalateModal(true)
              },
              hidden: !canEscalate,
            },
            {
              key: 'escalate-to-legal',
              label: t('escalate_modal.to_legal'),
              onClick: () => {
                setEscalateToLegal(true)
                setShowEscalateModal(true)
              },
              hidden: debtCase.stage !== 'final_notice',
            },
            {
              key: 'resolve',
              label: t('actions.resolve'),
              onClick: () => setShowResolveModal(true),
              hidden: !canResolve,
            },
            {
              key: 'generate-legal-notice',
              label: t('actions.generate_legal_notice'),
              onClick: handleGenerateLegalNotice,
            },
          ]}
        />

        {/* Two-column top section */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Case Summary */}
          <Card>
            <h3 className="font-semibold text-base mb-4">{t('case_summary.title')}</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('fields.stage')}</dt>
                <dd>
                  <Badge variant={getStageBadgeVariant(debtCase.stage)}>
                    {t(`stages.${debtCase.stage}`)}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('fields.created_at')}</dt>
                <dd className="font-medium text-sm">{formatDate(debtCase.created_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('fields.escalated_at')}</dt>
                <dd className="font-medium text-sm">{formatDate(debtCase.escalated_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('case_summary.days_overdue')}</dt>
                <dd className="font-medium text-sm">{debtCase.days_overdue}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('fields.notice_sent_at')}</dt>
                <dd className="font-medium text-sm">{formatDate(debtCase.notice_sent_at)}</dd>
              </div>
            </dl>
          </Card>

          {/* Invoice Details */}
          <Card>
            <h3 className="font-semibold text-base mb-4">{t('invoice_details.title')}</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('fields.invoice')}</dt>
                <dd className="font-mono font-medium text-sm">{debtCase.invoice_number}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('invoice_details.due')}</dt>
                <dd className="font-medium text-sm">
                  {debtCase.invoice ? formatDate(debtCase.invoice.due_at) : '—'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('invoice_details.amount')}</dt>
                <dd className="font-medium text-sm">
                  {formatBaht(debtCase.invoice?.total ?? 0, { decimals: 2 })}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('invoice_details.paid')}</dt>
                <dd className="font-medium text-sm">
                  {formatBaht(amountPaid, { decimals: 2 })}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-base-content/60">{t('invoice_details.balance')}</dt>
                <dd className="font-medium text-sm text-error">
                  {formatBaht(Number(debtCase.amount_due), { decimals: 2 })}
                </dd>
              </div>
            </dl>
            <div className="mt-4">
              <Button
                as={Link}
                to={`/invoices/${debtCase.invoice_id}`}
                variant="outline"
                size="sm"
                rightIcon={<ArrowUpRight className="w-3 h-3" />}
              >
                {t('actions.view_invoice')}
              </Button>
            </div>
          </Card>
        </div>

        {/* Escalation Timeline */}
        <Card>
          <h3 className="font-semibold text-base mb-4">{t('timeline.title')}</h3>
          <EscalationTimeline debtCase={debtCase} t={t} formatDate={formatDate} />
        </Card>

        {/* Notes Log */}
        <Card>
          <h3 className="font-semibold text-base mb-4">{t('notes.title')}</h3>
          {debtCase.notes ? (
            <pre className="whitespace-pre-wrap text-sm text-base-content font-sans bg-base-200 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
              {debtCase.notes}
            </pre>
          ) : (
            <p className="text-sm text-base-content/50 mb-4">{t('notes.empty')}</p>
          )}

          <div className="space-y-3">
            <Textarea
              rows={3}
              placeholder={t('notes.placeholder')}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleAddNote}
                loading={addNote.isPending}
                disabled={!noteText.trim()}
              >
                {t('actions.add_note')}
              </Button>
              <Button
                variant="outline"
                onClick={handleMarkNoticeSent}
                loading={markNoticeSent.isPending}
              >
                {t('actions.mark_notice_sent')}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Escalate Modal */}
      <Modal
        isOpen={showEscalateModal}
        onClose={() => setShowEscalateModal(false)}
        title={t('escalate_modal.title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowEscalateModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={handleEscalate} loading={escalate.isPending}>
              {t('escalate_modal.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-base-content/60">{t('escalate_modal.current_stage')}</span>
            <Badge variant={getStageBadgeVariant(debtCase.stage)}>
              {t(`stages.${debtCase.stage}`)}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-base-content/60">{t('escalate_modal.target_stage')}</span>
            <Badge variant={getStageBadgeVariant(escalateToLegal ? 'legal' : (nextStage ?? 'legal'))}>
              {t(`stages.${escalateToLegal ? 'legal' : (nextStage ?? 'legal')}`)}
            </Badge>
          </div>
        </div>
      </Modal>

      {/* Resolve Modal */}
      <Modal
        isOpen={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        title={t('actions.resolve')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowResolveModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={handleResolve} loading={resolve.isPending}>
              {t('escalate_modal.to_resolved')}
            </Button>
          </>
        }
      >
        <p className="text-sm">{t('resolve_confirm')}</p>
      </Modal>
    </>
  )
}
